/**
 * FormsSubTab - TASK-229, TASK-230, TASK-376
 * Shows form status cards with overview and direct navigation to full forms
 * Enhanced with section chips, progress bar, PDF download, and
 * TASK-376 Client Profile & Form A generation from approved KYC
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  User,
  Shield,
  Building,
  ChevronRight,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Download,
  Edit3,
} from 'lucide-react';
import { toast } from 'sonner';
import riskProfileService from '../../services/riskProfileService';
import { downloadPdfWithAuth } from '../../utils/downloadPdfWithAuth';
import { logger } from '../../utils/logger';
import { useAllFormStatuses } from '../../hooks/useCompliance';
import { useComplianceRole } from '../../hooks/useComplianceRole';
import type { FormType, FormStatus } from '../../types/clientCompliance';
import RiskProfileTab from '../ClientProfile/tabs/RiskProfileTab';
import KYCTab from '../ClientProfile/tabs/KYCTab';
import { ClientProfileForm } from './forms/ClientProfileForm';
import type { ClientProfileFormData } from './forms/ClientProfileForm';
import { FormAForm } from './forms/FormAForm';
import type { FormAFormData } from './forms/FormAForm';
import kycService from '../../services/kycService';

interface FormsSubTabProps {
  clientId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  onNavigateToDocuments?: () => void;
}

interface FormCardConfig {
  id: FormType;
  title: string;
  description: string;
  icon: React.ElementType;
  taskId?: string;
  sections: string[]; // TASK-230: Section names for chips
}

const FORM_CONFIGS: FormCardConfig[] = [
  {
    id: 'kyc',
    title: 'KYC Profile',
    description: 'Know Your Customer verification and identity documentation',
    icon: User,
    sections: ['Personal', 'Contact', 'Employment', 'Tax', 'PEP', 'Wealth', 'Income', 'Documents'],
  },
  {
    id: 'riskProfile',
    title: 'Risk Profile',
    description: 'Investment risk assessment and suitability questionnaire',
    icon: Shield,
    sections: ['Personal', 'Knowledge', 'Financial', 'Objectives', 'Risk', 'Profile', 'Strategy', 'Declarations', 'Signatures', 'Review'],
  },
  {
    id: 'clientProfile',
    title: 'Client Profile',
    description: 'Comprehensive client information and preferences',
    icon: FileText,
    taskId: 'TASK-376',
    sections: ['Account', 'Management', 'Beneficial Owner', 'POA', 'Structure'],
  },
  {
    id: 'formA',
    title: 'Form A - Beneficial Owner',
    description: 'Beneficial ownership declaration form (CDB 20)',
    icon: Building,
    taskId: 'TASK-376',
    sections: ['Account Holder', 'Beneficial Owner', 'Declaration', 'Signature'],
  },
];

const getStatusConfig = (status?: FormStatus['status']) => {
  switch (status) {
    case 'completed':
      return {
        icon: CheckCircle,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        label: 'Completed',
      };
    case 'submitted':
      return {
        icon: Clock,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        label: 'Submitted — Awaiting Review',
      };
    case 'in_progress':
      return {
        icon: Clock,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        label: 'In Progress',
      };
    case 'changes_requested':
      return {
        icon: AlertCircle,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        label: 'Changes Requested',
      };
    case 'expired':
      return {
        icon: AlertCircle,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        label: 'Expired',
      };
    case 'rejected':
      return {
        icon: XCircle,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        label: 'Rejected',
      };
    default:
      return {
        icon: FileText,
        color: 'text-gray-400',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        label: 'Not Started',
      };
  }
};

const FormCard: React.FC<{
  config: FormCardConfig;
  status?: FormStatus;
  clientId: string;
  onOpen: () => void;
}> = ({ config, status, clientId, onOpen }) => {
  const statusConfig = getStatusConfig(status?.status);
  const StatusIcon = statusConfig.icon;
  const FormIcon = config.icon;

  const handleDownloadPDF = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (config.id === 'kyc') {
        await kycService.downloadKYCPDF(clientId);
        toast.success('KYC PDF downloaded');
      } else if (config.id === 'riskProfile') {
        // TASK-931: this toasted "coming soon" while `riskProfileService.generatePDF` and the
        // route behind it had existed since TASK-179 — the form one tab over has called them
        // all along. Nothing was missing except the call.
        const result = await riskProfileService.generatePDF(clientId);
        await downloadPdfWithAuth(result.pdf_url, result.filename);
        toast.success('Risk profile PDF downloaded');
      } else {
        toast.info('PDF download coming soon');
      }
    } catch (error) {
      toast.error('Failed to download PDF');
      logger.error('PDF download error:', error);
    }
  };

  return (
    <div
      className={`border rounded-lg p-4 transition-shadow hover:shadow-md cursor-pointer ${statusConfig.borderColor}`}
      onClick={onOpen}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${statusConfig.bgColor}`}>
            <FormIcon className={`w-5 h-5 ${statusConfig.color}`} />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{config.title}</h3>
            <p className="text-sm text-gray-500 line-clamp-1">
              {config.description}
            </p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </div>

      {/* Version & Review Metadata */}
      {(status?.version || status?.lastUpdated || status?.nextReviewDate) && (
        <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-3">
          {status?.version && <span>v{status.version}</span>}
          {status?.lastUpdated && (
            <span>Updated: {new Date(status.lastUpdated).toLocaleDateString()}</span>
          )}
          {status?.nextReviewDate && (
            <span className={
              new Date(status.nextReviewDate) < new Date() ? 'text-red-600 font-medium' : ''
            }>
              Review: {new Date(status.nextReviewDate).toLocaleDateString()}
            </span>
          )}
        </div>
      )}

      {/* Status and actions */}
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2">
          <StatusIcon className={`w-4 h-4 ${statusConfig.color}`} />
          <span className={`text-sm font-medium ${statusConfig.color}`}>
            {statusConfig.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {status?.status === 'completed' && (
            <button
              onClick={handleDownloadPDF}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              title="Download PDF"
            >
              <Download className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            title="Edit form"
          >
            <Edit3 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export const FormsSubTab: React.FC<FormsSubTabProps> = ({
  clientId,
  clientName,
}) => {
  const navigate = useNavigate();
  const [activeForm, setActiveForm] = useState<FormType | null>(null);
  const formStatuses = useAllFormStatuses();
  const { visibleForms } = useComplianceRole();

  // TASK-376: Form data state for Client Profile and Form A
  const [clientProfileData, setClientProfileData] = useState<ClientProfileFormData | null>(null);
  const [formAData, setFormAData] = useState<FormAFormData | null>(null);
  // TASK-376: Load existing Client Profile and Form A data
  const loadFormData = useCallback(async () => {
    try {
      const [profileResult, formAResult] = await Promise.allSettled([
        kycService.getClientProfile(clientId),
        kycService.getFormA(clientId),
      ]);

      if (profileResult.status === 'fulfilled' && profileResult.value?.data) {
        setClientProfileData(profileResult.value.data);
      }
      if (formAResult.status === 'fulfilled' && formAResult.value?.data) {
        setFormAData(formAResult.value.data);
      }
    } catch (error) {
      logger.error('Error loading form data:', error);
    }
  }, [clientId]);

  useEffect(() => {
    loadFormData();
  }, [loadFormData]);


  // If a form is selected, show full form view
  if (activeForm) {
    return (
      <div>
        {/* Back button */}
        <button
          onClick={() => setActiveForm(null)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Back to Forms Overview
        </button>

        {/* Full form content */}
        {activeForm === 'kyc' && (
          <KYCTab clientId={clientId} clientName={clientName} />
        )}
        {activeForm === 'riskProfile' && (
          <RiskProfileTab clientId={clientId} clientName={clientName} />
        )}
        {activeForm === 'clientProfile' && (
          <ClientProfileForm
            clientId={clientId}
            clientName={clientName}
            initialData={clientProfileData}
            onSaved={loadFormData}
          />
        )}
        {activeForm === 'formA' && (
          <FormAForm
            clientId={clientId}
            clientName={clientName}
            initialData={formAData}
            onSaved={loadFormData}
          />
        )}
      </div>
    );
  }

  // Show form cards overview
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Compliance Forms
          </h3>
          <p className="text-sm text-gray-500">
            {FORM_CONFIGS.filter((c) => visibleForms.includes(c.id) && formStatuses[c.id]?.status === 'completed').length} of {FORM_CONFIGS.filter((c) => visibleForms.includes(c.id)).length} forms completed
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FORM_CONFIGS
          .filter((config) => visibleForms.includes(config.id))
          .map((config) => {
          return (
            <FormCard
              key={config.id}
              config={config}
              status={formStatuses[config.id]}
              clientId={clientId}
              onOpen={() => {
                if (config.id === 'kyc') {
                  const status = formStatuses.kyc?.status;
                  if (status && status !== 'not_started') {
                    setActiveForm('kyc');
                  } else {
                    navigate(`/clients/${clientId}/kyc-workspace`);
                  }
                } else {
                  setActiveForm(config.id);
                }
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

export default FormsSubTab;
